import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { inject, observer } from 'mobx-react'
import { Img } from 'react-image'

import { APIURL, PAGEURL } from '../../constants'
import PageTitle from '../common/PageTitle'
import { SelectedTier } from '../subscription/MembershipPlan'
import { SIGNUP_DESCRIPTION } from '../home/Constants'
import { ActionPageLoader } from '../../components/loaders'
import services from '../../components/services'

import { Link as ChaliceLink } from '../theme/button/Button'

const Steps = ({ gotoPlans }) => {
  const { pathname } =
    typeof window !== 'undefined' ? window.location : PAGEURL.SIGNUP
  return (
    <div className="steps">
      <div
        className={classNames('step', {
          active: pathname === PAGEURL.SIGNUP
        })}
        onClick={() => gotoPlans()}
      >
        <h2>Step 1</h2>
      </div>
      <div
        className={classNames('step', {
          active: pathname === PAGEURL.SIGNUP_REGISTER
        })}
      >
        <h2>Step 2</h2>
      </div>
      <div
        className={classNames('step', {
          active: pathname === PAGEURL.SIGNUP_ADD_ONS
        })}
      >
        <h2>Step 3</h2>
      </div>
    </div>
  )
}

Steps.propTypes = {
  gotoPlans: PropTypes.func
}

const SignupBlock = inject('store')(
  observer((props) => {
    let content = null
    if (props.rc) {
      content = (
        <div key="blank">
          <h1>Register to Request Consultation</h1>
          <div className="pricing-table pricing-wide pricing-center">
            <div className="tier-form">
              <div className="tier-top">
                <div className="tier-box">
                  <div className="tier-content"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    } else {
      content = (
        <div key="blank">
          <h1>Setup your Membership</h1>
          <Steps gotoPlans={props.gotoPlans} />
          <div className="pricing-table pricing-wide">
            <div className="tier-form">
              <div className="tier-top">
                <div className="tier-box">
                  <div className="tier-content"></div>
                </div>
              </div>
            </div>
            <div className="tier-form">
              <SelectedTier
                show_description={false}
                show_features={true}
                plan={props.selectedPlan}
                frequency={props.selectedFrequency}
                organization_invite={props.organization_invite}
                organization_name={props.organization_name}
              />
            </div>
          </div>
        </div>
      )
    }
    return (
      <div>
        <PageTitle
          title="Chalice Network - Membership Signup"
          description={SIGNUP_DESCRIPTION}
        />
        <div key="blank">{content}</div>
      </div>
    )
  })
)

SignupBlock.propTypes = {
  store: PropTypes.object,
  gotoSuccess: PropTypes.func,
  gotoPlans: PropTypes.func,
  frequency: PropTypes.string,
  coupon: PropTypes.string,
  rc: PropTypes.bool,
  selectedPlan: PropTypes.number,
  selectedFrequency: PropTypes.string,
  organization_name: PropTypes.string,
  organization_invite: PropTypes.bool,
  invite_email: PropTypes.string,
  plan: PropTypes.object
}

const loginUrl = () => {
  const localStorage = typeof window !== 'undefined' && window.localStorage
  const next = localStorage.next
  if (next) {
    return `${PAGEURL.OIDC_AUTHENTICATE_LOGIN}?next=${encodeURIComponent(next)}`
  } else {
    return PAGEURL.OIDC_AUTHENTICATE_LOGIN
  }
}

const backLink = () => {
  const loginLink = loginUrl()
  if (loginLink === '/') {
    return (
      <div>
        <span className="hidden-xs">Already a member?</span>
        <Link href={loginLink}>
          <ChaliceLink>Sign In</ChaliceLink>
        </Link>
      </div>
    )
  } else {
    return (
      <div>
        <span className="hidden-xs">Already a member?</span>
        <ChaliceLink href={loginLink}>Sign In</ChaliceLink>
      </div>
    )
  }
}

const RenderLogo = inject('store')(
  observer((props) => {
    const { siteData } = props.store

    const branding = siteData.get('logo_media_center')
      ? (<Img className="header-logo" src={siteData.get('logo_media_center').media} alt={siteData.get('name')} />)
      : (<span>{siteData.get('name')}</span>)

    return (
      <Link href="/">
        <a className="logo-link">
          { branding }
        </a>
      </Link>
    )
  })
)

const RenderNav = inject('store')(
  observer((props) => {
    return (
      <nav
        className={classNames('home-navbar', { 'admin-navbar': props.admin })}
      >
        <div className="navbar-content">
          <div className="navbar-home-left">
            <RenderLogo />
          </div>
          <div className={classNames('navbar-home-right')}>
            <div
              className={classNames('flex-row', {
                'display-none': props.store.isAuthenticated()
              })}
            >
              {backLink()}
            </div>
          </div>
        </div>
      </nav>
    )
  })
)

const SignupFooter = inject('store')(
  observer(({ store }) => {
    if (!store.siteData) {
      return null
    }
    return (
      <div className="signup-footer">
        {store.siteData.get('help_link') && (
          <a key="help" href={store.siteData.get('help_link')}>
            Help
          </a>
        )}
        {store.siteData.get('privacy_link') && (
          <a key="privacy" href={store.siteData.get('privacy_link')}>
            Privacy Policy
          </a>
        )}
        {store.siteData.get('terms_link') && (
          <a href={store.siteData.get('terms_link')}>Terms of Service</a>
        )}
      </div>
    )
  })
)

const Salutation = inject('store')(
  observer((props) => {
    let s = ''
    const { userData } = props.store.sessionData
    if (userData && userData.first_name) {
      s = `${userData.first_name}`
    }
    return `Welcome ${s}!`.trim()
  })
)

const Register = inject('store')(
  observer((props) => {
    const router = useRouter()
    const [organizationInvite, setOrganizationInvite] = useState(false)
    const [organizationName, setOrganizationName] = useState('')
    const [inviteEmail, setInviteEmail] = useState('')
    const query = router.query || {}
    const frequency = query.freq || 'monthly'
    const coupon = query.coupon || null
    const rc = query.rc === '1'
    const [plan, setPlan] = useState(null)

    useEffect(() => {
      const setup = async () => {
        if (query.invite_id) {
          const res = await services.get_v2(
            APIURL.API_GET_ORGANIZATION_BY_INVITE_ID,
            {
              params: {
                invite_id: query.invite_id
              }
            }
          )
          if (res.statusCode === 200) {
            setInviteEmail(res.response.email)
            setOrganizationName(res.response.name)
            setOrganizationInvite(query.invite_id)
          }
        }
      }
      setup()
    }, [])

    const gotoPlans = (e) => {
      router.push(PAGEURL.SIGNUP)
    }

    const gotoSuccess = (e, plan) => {
      const localStorage = typeof window !== 'undefined' && window.localStorage
      localStorage.removeItem('invite_id')
      if (!props.store.isWhiteLabel) {
        router.push(PAGEURL.SIGNUP_ADD_ONS)
      } else {
        router.push(PAGEURL.SIGNUP_SUCCESS(plan.id))
      }
    }

    if (!plan) {
      return <ActionPageLoader />
    }

    return (
      <SignupBlock
        store={props.store}
        gotoSuccess={gotoSuccess}
        gotoPlans={gotoPlans}
        frequency={frequency}
        coupon={coupon}
        rc={rc}
        selectedPlan={props.store.subscription.plan}
        selectedFrequency={props.store.subscription.billing_frequency}
        organization_invite={organizationInvite}
        organization_name={organizationName}
        invite_email={inviteEmail}
        plan={plan}
      />
    )
  })
)

export { RenderNav, Register, SignupFooter, Steps, SignupBlock, Salutation }
